/**
 * `PropertiesTab` — Bloom-inspired two-column property layout.
 *
 * Rows render as [KEY][VALUE] side-by-side with values right-aligned and
 * long values collapsed into a "Show more"/"Show less" disclosure (same
 * disclosure UX as `PropertyItem` uses for chunk nodes).
 *
 * Handles:
 *  - view mode for node properties (plain values, dates, numbers, booleans,
 *    URLs, emails, paths, and images)
 *  - edit mode (change/remove/add), save via `api.graph.updateNodeProperties`
 *  - suspect toggle for `Person` nodes (persisted through the same endpoint)
 *  - read-only edge property rendering (with source → target summary)
 *
 * The `source_document_path` property is intentionally hidden — per the UX
 * spec its clickable link moves into the dedicated Source Documents tab.
 */

import {
  Check,
  ChevronDown,
  ChevronRight as ChevronRightIcon,
  ExternalLink,
  Loader2,
  Pencil,
  Plus,
  XCircle,
  X as XIcon,
} from 'lucide-react'
import { useCallback, useState } from 'react'
import { toast } from 'sonner'
import { LABEL_COLORS } from '@/components/neo4j/cytoscape/entity-icons'
import type { SelectedEdge, SelectedNode } from '@/components/neo4j/cytoscape/types'
import { AuthedImageDisplay } from '@/components/neo4j/node-details/components/AuthedImageDisplay'
import { filterProperties, sortProperties } from '@/components/neo4j/shared/PropertyItem'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { ManualProvenance } from '@/features/manual-graph/ManualProvenance'
import { api } from '@/services/api'
import { cn } from '@/utils/cn'

interface LegendLabel {
  label: string
  count: number
  color: string
  borderColor?: string
}

export interface PropertiesTabProps {
  caseId: string
  selectedNode: SelectedNode | null
  selectedEdge: SelectedEdge | null
  fullNodeDetails?: { properties?: Record<string, unknown>; labels?: string[] } | null
  isLoadingNodeDetails?: boolean
  legendLabels?: LegendLabel[]
  onPropertyUpdate?: (nodeId: string, updatedProperties: Record<string, unknown>) => void
}

const IMAGE_KEYS = new Set([
  // Face nodes carry their crop under `crop_path` — render it like any other
  // node image instead of printing the raw `/api/v1/files/...` path.
  'crop_path',
  'cropPath',
  'image_path',
  'imagePath',
  'image_url',
  'imageUrl',
  'photo_path',
  'photoPath',
  'photo',
  'image',
  'thumbnail',
  'picture',
])

const LONG_TEXT_THRESHOLD = 160
const LONG_TEXT_PREVIEW = 120

// Excluded from the Properties tab because the data surfaces in its own tab or
// header. The `source_document_path` link now lives in Source Documents.
const HIDDEN_KEYS = new Set(['source_document_path', 'source_document_id'])

// Only absolute URLs are servable: `http(s)://...` or `/api/v1/files/<s3_key>`
// written by the extraction pipeline. Pre-v2 filesystem paths have no backing
// mount anymore — treat them as plain text, not images.
function imageSrcFor(value: string): string {
  return value
}

function looksLikeImage(value: string): boolean {
  if (!value) return false
  return value.startsWith('http') || value.startsWith('/api/')
}

function humaniseKey(key: string): string {
  return key.replace(/_/g, ' ')
}

function labelStyle(label: string, legend: LegendLabel[] | undefined): React.CSSProperties {
  const hit = legend?.find(l => l.label === label)
  if (hit) {
    return {
      backgroundColor: hit.color,
      color: 'var(--primary-foreground)',
      borderColor: hit.borderColor ?? hit.color,
    }
  }
  const colors = LABEL_COLORS[label] ?? LABEL_COLORS.Default
  if (!colors) {
    return { backgroundColor: 'var(--muted)', color: 'var(--foreground)' }
  }
  return { backgroundColor: colors.fill, color: '#ffffff', borderColor: colors.stroke }
}

function isUrl(value: string): boolean {
  return value.startsWith('http://') || value.startsWith('https://')
}

function isEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
}

function isDateLike(value: string): boolean {
  return !Number.isNaN(Date.parse(value)) && /^\d{4}-\d{2}-\d{2}/.test(value)
}

/** True when the value names a calendar day and carries no time of day. */
function isDateOnly(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(value.trim())
}

function formatDate(value: string): string {
  const d = new Date(value)
  // A date-only value names a day, not an instant, so it is rendered without a
  // clock and read back in UTC. `new Date('2011-03-09')` is UTC midnight: shown
  // with a time in a zone ahead of UTC it printed "9. 3. 2011, 01:00" — an hour
  // the source never carried — and in a zone behind UTC it lands on the
  // previous day, which is worse than a stray time.
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...(isDateOnly(value)
      ? { timeZone: 'UTC' }
      : { hour: '2-digit' as const, minute: '2-digit' as const }),
  })
}

export function PropertiesTab({
  caseId,
  selectedNode,
  selectedEdge,
  fullNodeDetails,
  isLoadingNodeDetails,
  legendLabels,
  onPropertyUpdate,
}: PropertiesTabProps) {
  const [isEditMode, setIsEditMode] = useState(false)
  const [editedProperties, setEditedProperties] = useState<Record<string, unknown>>({})
  const [isSaving, setIsSaving] = useState(false)
  const [newKey, setNewKey] = useState('')
  const [newValue, setNewValue] = useState('')

  const isPersonNode = Boolean(selectedNode?.labels?.includes('Person'))

  const enterEditMode = useCallback(() => {
    const base = (fullNodeDetails?.properties ?? selectedNode?.properties ?? {}) as Record<
      string,
      unknown
    >
    setEditedProperties({ ...base })
    setIsEditMode(true)
    setNewKey('')
    setNewValue('')
  }, [fullNodeDetails, selectedNode])

  const cancelEdit = useCallback(() => {
    setIsEditMode(false)
    setEditedProperties({})
    setNewKey('')
    setNewValue('')
  }, [])

  const changeProperty = useCallback((key: string, value: string) => {
    setEditedProperties(prev => ({ ...prev, [key]: value }))
  }, [])

  const removeProperty = useCallback((key: string) => {
    setEditedProperties(prev => {
      const next = { ...prev }
      delete next[key]
      return next
    })
  }, [])

  const addProperty = useCallback(() => {
    if (!newKey.trim()) return
    setEditedProperties(prev => ({ ...prev, [newKey.trim()]: newValue }))
    setNewKey('')
    setNewValue('')
  }, [newKey, newValue])

  const saveProperties = useCallback(async () => {
    if (!selectedNode) return
    setIsSaving(true)
    try {
      const originalProps = (fullNodeDetails?.properties ??
        selectedNode.properties ??
        {}) as Record<string, unknown>
      const removed = Object.keys(originalProps).filter(
        key => !(key in editedProperties) && filterProperties(key, false)
      )
      await api.graph.updateNodeProperties(caseId, selectedNode.id, {
        properties: editedProperties,
        remove_properties: removed,
      })
      setIsEditMode(false)
      toast.success('Properties saved successfully')
      onPropertyUpdate?.(selectedNode.id, editedProperties)
    } catch (err) {
      console.error('Failed to save properties:', err)
      toast.error('Failed to save properties')
    } finally {
      setIsSaving(false)
    }
  }, [caseId, selectedNode, fullNodeDetails, editedProperties, onPropertyUpdate])

  if (selectedEdge && !selectedNode) {
    return <EdgePropertyView selectedEdge={selectedEdge} legendLabels={legendLabels} />
  }

  if (!selectedNode) return null

  const allProps = (fullNodeDetails?.properties ?? selectedNode.properties ?? {}) as Record<
    string,
    unknown
  >
  const resolvedLabels = fullNodeDetails?.labels ?? selectedNode.labels ?? []

  return (
    <div className="space-y-4">
      {isLoadingNodeDetails ? (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="text-xs">Loading details...</span>
        </div>
      ) : null}

      {resolvedLabels.length > 0 ? (
        <div>
          <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Labels
          </span>
          <div className="mt-1.5 flex flex-wrap gap-1">
            {resolvedLabels.map(label => (
              <span
                key={label}
                className="inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider"
                style={labelStyle(label, legendLabels)}
              >
                {label}
              </span>
            ))}
          </div>
        </div>
      ) : null}

      <div>
        <div className="mb-2 flex items-center justify-between">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Properties
          </span>
          {!isEditMode ? (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground max-md:h-9 max-md:px-3"
              onClick={enterEditMode}
            >
              <Pencil className="mr-1 h-3 w-3" />
              Edit
            </Button>
          ) : null}
        </div>

        {isEditMode ? (
          <PropertyEditSection
            isPersonNode={isPersonNode}
            editedProperties={editedProperties}
            onToggleSuspect={checked =>
              setEditedProperties(prev => ({ ...prev, is_suspect: checked }))
            }
            onChangeProperty={changeProperty}
            onRemoveProperty={removeProperty}
            newKey={newKey}
            setNewKey={setNewKey}
            newValue={newValue}
            setNewValue={setNewValue}
            onAddProperty={addProperty}
            onCancel={cancelEdit}
            onSave={saveProperties}
            isSaving={isSaving}
          />
        ) : (
          <PropertyViewSection
            allProps={allProps}
            isPersonNode={isPersonNode}
            selectedNode={selectedNode}
          />
        )}
      </div>
    </div>
  )
}

/* ────────────────────── view section ────────────────────── */

function PropertyViewSection({
  allProps,
  isPersonNode,
  selectedNode,
}: {
  allProps: Record<string, unknown>
  isPersonNode: boolean
  selectedNode: SelectedNode
}) {
  const entries = Object.entries(allProps)
    .filter(
      ([key]) => filterProperties(key, false) && key !== 'is_suspect' && !HIDDEN_KEYS.has(key)
    )
    .sort(sortProperties)

  const rows: React.ReactNode[] = []

  if (isPersonNode) {
    rows.push(
      <PropertyRow
        key="__suspect"
        k="Suspect"
        value={selectedNode.properties?.is_suspect === true ? 'Yes' : 'No'}
      />
    )
  }

  entries.forEach(([key, value]) => {
    const stringValue = String(value ?? '')
    const isImg = IMAGE_KEYS.has(key) && looksLikeImage(stringValue)
    if (isImg) {
      rows.push(
        <div key={key} className="space-y-2 px-4 py-2.5">
          <span className="block text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            {humaniseKey(key)}
          </span>
          <AuthedImageDisplay
            src={imageSrcFor(stringValue)}
            alt={selectedNode.name || 'Node image'}
            className="max-h-[300px] w-full rounded-md"
            aspectRatio="auto"
          />
        </div>
      )
      return
    }
    rows.push(<PropertyRow key={key} k={humaniseKey(key)} value={value} />)
  })

  return (
    <>
      {/* A hand-made entity says who added it, above whatever it holds. */}
      <ManualProvenance properties={allProps} className="mb-2" />
      {rows.length === 0 ? (
        <p className="rounded-lg border border-border px-3 py-3 text-center text-xs text-muted-foreground">
          No properties.
        </p>
      ) : (
        <dl className="divide-y divide-border overflow-hidden rounded-lg border border-border">
          {rows}
        </dl>
      )}
    </>
  )
}

interface PropertyRowProps {
  k: string
  value: unknown
}

function PropertyRow({ k, value }: PropertyRowProps) {
  const [expanded, setExpanded] = useState(false)

  if (value === null || value === undefined) {
    return (
      <Row k={k}>
        <span className="italic text-muted-foreground/80">null</span>
      </Row>
    )
  }

  if (typeof value === 'boolean') {
    return (
      <Row k={k}>
        <span
          className={cn(
            'inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium',
            value
              ? 'border-primary/40 bg-primary/10 text-primary'
              : 'border-border bg-muted/40 text-muted-foreground'
          )}
        >
          {value ? 'Yes' : 'No'}
        </span>
      </Row>
    )
  }

  if (typeof value === 'number') {
    return (
      <Row k={k}>
        <span className="font-mono tabular-nums text-foreground">{value.toLocaleString()}</span>
      </Row>
    )
  }

  const str = String(value ?? '')

  if (isUrl(str)) {
    return (
      <Row k={k}>
        <a
          href={str}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex max-w-full items-center gap-1 truncate text-primary hover:underline"
          title={str}
        >
          <ExternalLink className="h-3 w-3 shrink-0" />
          <span className="truncate">{str}</span>
        </a>
      </Row>
    )
  }

  if (isEmail(str)) {
    return (
      <Row k={k}>
        <a
          href={`mailto:${str}`}
          className="max-w-full truncate text-primary hover:underline"
          title={str}
        >
          {str}
        </a>
      </Row>
    )
  }

  if (isDateLike(str)) {
    return (
      <Row k={k}>
        <span className="font-mono text-xs tabular-nums text-foreground">{formatDate(str)}</span>
      </Row>
    )
  }

  if (str.length > LONG_TEXT_THRESHOLD) {
    return (
      <Row k={k}>
        <button
          type="button"
          onClick={() => setExpanded(v => !v)}
          className="inline-flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground"
        >
          {expanded ? (
            <ChevronDown className="h-3 w-3 shrink-0 text-primary" />
          ) : (
            <ChevronRightIcon className="h-3 w-3 shrink-0 text-primary" />
          )}
          <span>{expanded ? 'Collapse' : `Show ${str.length.toLocaleString()} chars`}</span>
        </button>
        <div
          className={cn(
            'mt-1 whitespace-pre-wrap break-words text-xs text-foreground',
            !expanded && 'line-clamp-3'
          )}
        >
          {expanded ? str : str.slice(0, LONG_TEXT_PREVIEW)}
          {!expanded && str.length > LONG_TEXT_PREVIEW ? '…' : ''}
        </div>
      </Row>
    )
  }

  return (
    <Row k={k}>
      <span className="whitespace-pre-wrap break-words text-foreground">{str}</span>
    </Row>
  )
}

function Row({ k, children }: { k: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[140px_minmax(0,1fr)] items-start gap-3 px-3 py-2 text-xs">
      <dt className="truncate text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
        {k}
      </dt>
      <dd className="min-w-0 break-words text-xs text-foreground">{children}</dd>
    </div>
  )
}

/* ────────────────────── edit section ────────────────────── */

interface PropertyEditSectionProps {
  isPersonNode: boolean
  editedProperties: Record<string, unknown>
  onToggleSuspect: (checked: boolean) => void
  onChangeProperty: (key: string, value: string) => void
  onRemoveProperty: (key: string) => void
  newKey: string
  setNewKey: (v: string) => void
  newValue: string
  setNewValue: (v: string) => void
  onAddProperty: () => void
  onCancel: () => void
  onSave: () => void
  isSaving: boolean
}

function PropertyEditSection(props: PropertyEditSectionProps) {
  const {
    isPersonNode,
    editedProperties,
    onToggleSuspect,
    onChangeProperty,
    onRemoveProperty,
    newKey,
    setNewKey,
    newValue,
    setNewValue,
    onAddProperty,
    onCancel,
    onSave,
    isSaving,
  } = props
  return (
    <div className="space-y-3 rounded-md border border-primary/30 bg-muted/30 p-3">
      {isPersonNode ? (
        <div className="border-b border-border pb-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="suspect-toggle-edit" className="text-xs text-muted-foreground">
              Mark as Suspect
            </Label>
            <Switch
              id="suspect-toggle-edit"
              checked={editedProperties.is_suspect === true}
              onCheckedChange={onToggleSuspect}
            />
          </div>
        </div>
      ) : null}
      {Object.entries(editedProperties)
        .filter(
          ([key]) => filterProperties(key, false) && key !== 'is_suspect' && !HIDDEN_KEYS.has(key)
        )
        .sort(sortProperties)
        .map(([key, value]) => (
          <div key={key} className="space-y-1">
            <div className="flex items-center justify-between">
              <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                {humaniseKey(key)}
              </label>
              <Button
                variant="ghost"
                size="sm"
                className="h-5 w-5 p-0 text-muted-foreground hover:text-destructive max-md:h-8 max-md:w-8"
                onClick={() => onRemoveProperty(key)}
                aria-label={`Remove ${key}`}
              >
                <XCircle className="h-3 w-3" />
              </Button>
            </div>
            <Input
              value={String(value ?? '')}
              onChange={e => onChangeProperty(key, e.target.value)}
              className="h-8 text-xs"
            />
          </div>
        ))}

      <div className="border-t border-border pt-2">
        <span className="mb-2 block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          Add Property
        </span>
        <div className="space-y-2">
          <Input
            placeholder="Property name"
            value={newKey}
            onChange={e => setNewKey(e.target.value)}
            className="h-8 text-xs"
          />
          <Input
            placeholder="Value"
            value={newValue}
            onChange={e => setNewValue(e.target.value)}
            className="h-8 text-xs"
          />
          <Button
            variant="outline"
            size="sm"
            className="h-7 w-full text-xs"
            onClick={onAddProperty}
            disabled={!newKey.trim()}
          >
            <Plus className="mr-1 h-3 w-3" />
            Add
          </Button>
        </div>
      </div>

      <div className="flex gap-2 border-t border-border pt-2">
        <Button
          variant="outline"
          size="sm"
          className="h-8 flex-1 text-xs"
          onClick={onCancel}
          disabled={isSaving}
        >
          <XIcon className="mr-1 h-3 w-3" />
          Cancel
        </Button>
        <Button size="sm" className="h-8 flex-1 text-xs" onClick={onSave} disabled={isSaving}>
          {isSaving ? (
            <Loader2 className="mr-1 h-3 w-3 animate-spin" />
          ) : (
            <Check className="mr-1 h-3 w-3" />
          )}
          Save
        </Button>
      </div>
    </div>
  )
}

/* ────────────────────── edge view ────────────────────── */

/* Register history: the dated register rows this edge folds together, written
 * by the CZ/SK register import as `_register_history` (a JSON array —
 * ADR-0017). It is `_`-prefixed so the generic list below hides it; this
 * section is its curated rendering. Values are the register's own, verbatim. */
interface RegisterHistoryRow {
  role?: string
  share?: string
  share_pct?: number
  deposit?: number
  deposit_currency?: string
  paid_up?: string
  since?: string
  until?: string
}

function parseRegisterHistory(value: unknown): RegisterHistoryRow[] {
  if (typeof value !== 'string' || value === '') return []
  try {
    const parsed: unknown = JSON.parse(value)
    if (!Array.isArray(parsed)) return []
    return parsed.filter(
      (row): row is RegisterHistoryRow => typeof row === 'object' && row !== null
    )
  } catch {
    return []
  }
}

function registerRowFacts(row: RegisterHistoryRow): string {
  const parts: string[] = []
  if (row.share_pct != null) parts.push(`${row.share_pct} %`)
  else if (row.share) parts.push(row.share)
  if (row.deposit != null)
    parts.push(`${row.deposit.toLocaleString()} ${row.deposit_currency ?? ''}`.trim())
  if (row.paid_up) parts.push(`paid up ${row.paid_up}`)
  return parts.join(' · ')
}

function RegisterHistorySection({ history }: { history: RegisterHistoryRow[] }) {
  if (history.length === 0) return null
  return (
    <div>
      <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        Register history
      </span>
      <ol className="mt-1.5 divide-y divide-border overflow-hidden rounded-lg border border-border">
        {history.map((row, i) => {
          const facts = registerRowFacts(row)
          return (
            <li
              key={`${row.since ?? ''}|${row.until ?? ''}|${i}`}
              className="flex items-baseline justify-between gap-3 px-2.5 py-1.5"
            >
              <div className="min-w-0">
                <div className="truncate text-xs text-foreground">{row.role ?? '—'}</div>
                {facts ? (
                  <div className="truncate text-[11px] text-muted-foreground">{facts}</div>
                ) : null}
              </div>
              <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground">
                {row.since ?? '…'} – {row.until ?? 'now'}
              </span>
            </li>
          )
        })}
      </ol>
    </div>
  )
}

function EdgePropertyView({
  selectedEdge,
  legendLabels,
}: {
  selectedEdge: SelectedEdge
  legendLabels: LegendLabel[] | undefined
}) {
  const entries = Object.entries(selectedEdge.properties ?? {}).filter(
    ([key]) => !key.startsWith('_') && !HIDDEN_KEYS.has(key)
  )
  // Synthetic header rows so the edge properties view mirrors the node one:
  // Type, From, To appear as the first three rows of the same property list.
  const headerRows: Array<[string, unknown]> = [
    ['type', selectedEdge.type],
    ['from', selectedEdge.sourceName ?? selectedEdge.source],
    ['to', selectedEdge.targetName ?? selectedEdge.target],
  ]
  const sortedProps = entries.sort(sortProperties)
  const registerHistory = parseRegisterHistory(selectedEdge.properties?._register_history)

  return (
    <div className="space-y-4">
      <ManualProvenance properties={selectedEdge.properties} />
      <div>
        <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          Properties
        </span>
        <dl className="mt-1.5 divide-y divide-border overflow-hidden rounded-lg border border-border">
          {headerRows.map(([k, v]) => (
            <PropertyRow key={`__${k}`} k={humaniseKey(k)} value={v} />
          ))}
          {sortedProps.map(([key, value]) => (
            <PropertyRow key={key} k={humaniseKey(key)} value={value} />
          ))}
        </dl>
      </div>
      <RegisterHistorySection history={registerHistory} />
    </div>
  )
}
